import { useState, useEffect } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveVideo } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';
import ResponsiveImage from './ResponsiveImage';

export default function AboutSection({ block = {}, brand, mediaFolders = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { content = {}, config = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const [activeTab, setActiveTab] = useState('mission');
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [carouselIdx, setCarouselIdx] = useState(0);

    // Carousel auto-advance (must be before early return — Rules of Hooks)
    const selectedImagesForEffect = (() => {
        const folderImgs = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
        const sel = content.selected_file_ids;
        return sel?.length > 0 ? folderImgs.filter(f => sel.includes(f.id)) : folderImgs;
    })();
    const mediaTypeForEffect = config.media_mode || config.media_type || 'image';
    useEffect(() => {
        if (mediaTypeForEffect !== 'carousel' || selectedImagesForEffect.length <= 1) return;
        const t = setInterval(() => setCarouselIdx(p => (p + 1) % selectedImagesForEffect.length), 4000);
        return () => clearInterval(t);
    }, [mediaTypeForEffect, selectedImagesForEffect.length]);

    if (!brand.mission && !brand.vision && !brand.welcome_text) return null;

    const tag = content.tag || 'Conocenos';
    const title = content.title || 'Nosotros';
    const missionLabel = content.mission_label || 'Mision';
    const visionLabel = content.vision_label || 'Vision';
    const folderImages = content.folder_id ? (mediaFolders[content.folder_id] || []) : [];
    const _sel = content.selected_file_ids;
    const selectedImages = _sel?.length > 0 ? folderImages.filter(f => _sel.includes(f.id)) : folderImages;
    // Support both ImagePicker (image_folder_id) and old FolderPicker (folder_id) for single image
    const coverImage = (() => {
        if (content.image_folder_id && content.image_file_id && mediaFolders[content.image_folder_id]) {
            const f = mediaFolders[content.image_folder_id].find(x => String(x.id) === String(content.image_file_id));
            if (f) return f;
        }
        if (content.image_url) return { image_url: content.image_url };
        return selectedImages[0] || null;
    })();
    const mediaType = config.media_mode || config.media_type || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const variant = config.variant || 'split';
    const anim = getAnimation(config, visible);

    // Video info
    const video = resolveVideo(content, 'video', mediaFolders);
    const videoThumb = (() => {
        if (content.video_thumb_folder_id && content.video_thumb_file_id && mediaFolders[content.video_thumb_folder_id]) {
            const f = mediaFolders[content.video_thumb_folder_id].find(x => String(x.id) === String(content.video_thumb_file_id));
            if (f) return f.image_url;
        }
        return content.video_thumb_url || video?.thumb || null;
    })();

    // Shared media panel component
    const MediaPanel = () => {
        const accent = s.accent;
        const placeholder = (
            <div className="relative w-full h-80 rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${hexToRgba(accent, 0.06)}, ${hexToRgba(accent, 0.15)})` }}>
                <div className="absolute inset-0 flex items-center justify-center">
                    <svg className="w-24 h-24 opacity-20" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                </div>
            </div>
        );

        if (mediaType === 'video' && video) {
            if (video.type === 'file') {
                return (
                    <div className="rounded-2xl overflow-hidden shadow-xl aspect-video bg-gray-900">
                        <video src={video.src} className={`w-full h-full ${fitClass}`} controls preload="metadata" />
                    </div>
                );
            }
            return (
                <>
                    <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-video bg-gray-900 cursor-pointer group" onClick={() => setShowVideoModal(true)}>
                        {videoThumb && <img src={videoThumb} alt="" className={`absolute inset-0 w-full h-full ${fitClass} group-hover:scale-105 transition-transform duration-500`} loading="lazy" />}
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110"
                                style={{ backgroundColor: accent, boxShadow: `0 0 0 8px ${hexToRgba(accent, 0.15)}` }}>
                                <svg className="w-7 h-7 md:w-8 md:h-8 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                        </div>
                    </div>
                    {showVideoModal && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-8" onClick={() => setShowVideoModal(false)}>
                            <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
                                <button onClick={() => setShowVideoModal(false)} className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10">
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                                    <iframe src={`${video.embed}?autoplay=1&rel=0`} className="absolute inset-0 w-full h-full" style={{ border: 'none' }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            );
        }

        if (mediaType === 'carousel' && selectedImages.length > 1) {
            return (
                <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
                    {selectedImages.map((img, i) => (
                        <ResponsiveImage key={img.id} src={img.image_url} variantUrls={img.variant_urls} width={img.width} height={img.height} alt={img.title || ''}
                            sizes="(max-width:1024px) 100vw, 50vw"
                            className={`absolute inset-0 w-full h-full ${fitClass} transition-opacity duration-700 ${i === carouselIdx % selectedImages.length ? 'opacity-100' : 'opacity-0'}`} />
                    ))}
                    {/* Dots */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                        {selectedImages.map((_, i) => (
                            <button key={i} onClick={() => setCarouselIdx(i)}
                                className={`w-2 h-2 rounded-full transition-all ${i === carouselIdx % selectedImages.length ? 'bg-white scale-125' : 'bg-white/50'}`} />
                        ))}
                    </div>
                </div>
            );
        }

        if (coverImage?.image_url) {
            return (
                <div className="relative">
                    <ResponsiveImage src={coverImage.image_url} variantUrls={coverImage.variant_urls} width={coverImage.width} height={coverImage.height} alt={coverImage.title || title} className={`w-full max-h-[500px] rounded-2xl shadow-xl ${fitClass}`} sizes="(max-width:1024px) 100vw, 50vw" />
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-2xl -z-10 opacity-20" style={{ backgroundColor: accent }} />
                    <div className="absolute -top-4 -left-4 w-24 h-24 rounded-2xl -z-10 opacity-10" style={{ backgroundColor: accent }} />
                </div>
            );
        }

        return placeholder;
    };

    // ── Timeline variant ──
    if (variant === 'timeline') {
        const items = [
            brand.mission && { label: missionLabel, text: brand.mission },
            brand.vision && { label: visionLabel, text: brand.vision },
        ].filter(Boolean);

        return (
            <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={tag} title={title} config={config} s={s} />
                    <div className="relative">
                        <div className="absolute left-6 top-0 bottom-0 w-px" style={{ backgroundColor: hexToRgba(s.accent, 0.19) }} />
                        <div className="space-y-12">
                            {items.map((item, i) => (
                                <div key={i} className={`relative pl-16 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                    style={{ transitionDelay: visible ? `${200 + i * 200}ms` : '0ms' }}>
                                    <div className="absolute left-3 w-7 h-7 rounded-full border-4 flex items-center justify-center"
                                        style={{ borderColor: s.accent, backgroundColor: s.dark ? '#1f2937' : '#ffffff' }}>
                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.accent }} />
                                    </div>
                                    <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{item.label}</h3>
                                    <p className="leading-relaxed text-base" style={{ color: s.textSub }}>{item.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Centered variant ──
    if (variant === 'centered') {
        return (
            <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center ${anim}`}>
                    <SectionHeader tag={tag} title={title} config={config} s={s} />
                    <div className="space-y-10">
                        {brand.mission && (
                            <div className="px-8 py-6 rounded-2xl" style={{ backgroundColor: hexToRgba(s.accent, 0.03) }}>
                                <h3 className="text-lg font-bold mb-3" style={{ color: s.accent }}>{missionLabel}</h3>
                                <p className="leading-relaxed text-base" style={{ color: s.textSub }}>{brand.mission}</p>
                            </div>
                        )}
                        {brand.vision && (
                            <div className="px-8 py-6 rounded-2xl" style={{ backgroundColor: hexToRgba(s.accent, 0.03) }}>
                                <h3 className="text-lg font-bold mb-3" style={{ color: s.accent }}>{visionLabel}</h3>
                                <p className="leading-relaxed text-base" style={{ color: s.textSub }}>{brand.vision}</p>
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Tabs variant ──
    if (variant === 'tabs') {
        const tabs = [
            brand.mission && { key: 'mission', label: missionLabel, text: brand.mission },
            brand.vision && { key: 'vision', label: visionLabel, text: brand.vision },
        ].filter(Boolean);

        return (
            <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={tag} title={title} config={config} s={s} />
                    <div className="flex justify-center gap-2 mb-8">
                        {tabs.map(t => (
                            <button key={t.key} onClick={() => setActiveTab(t.key)}
                                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${activeTab === t.key ? 'text-white shadow-lg' : ''}`}
                                style={activeTab === t.key ? { backgroundColor: s.accent } : { color: s.textSub, backgroundColor: 'transparent' }}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {tabs.map(t => activeTab === t.key && (
                        <div key={t.key} className="text-center max-w-2xl mx-auto animate-fade-in">
                            <p className="text-lg leading-relaxed" style={{ color: s.textSub }}>{t.text}</p>
                        </div>
                    ))}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}.animate-fade-in{animation:fadeIn .4s ease}`}</style>
            </section>
        );
    }

    // ── Cards variant ──
    if (variant === 'cards') {
        const items = [
            brand.mission && { label: missionLabel, text: brand.mission, icon: '🎯' },
            brand.vision && { label: visionLabel, text: brand.vision, icon: '🔭' },
        ].filter(Boolean);
        return (
            <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={tag} title={title} config={config} s={s} />
                    <div className={`grid gap-8 ${items.length === 1 ? 'max-w-lg mx-auto' : 'grid-cols-1 md:grid-cols-2'}`}>
                        {items.map((item, i) => (
                            <div key={i} className={`rounded-2xl p-8 border transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ borderColor: hexToRgba(s.accent, 0.13), transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                <div className="text-3xl mb-4">{item.icon}</div>
                                <h3 className="text-xl font-bold mb-3" style={{ color: s.accent }}>{item.label}</h3>
                                <p className="leading-relaxed" style={{ color: s.textSub }}>{item.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Fullwidth image ──
    if (variant === 'fullwidth-image') {
        return (
            <section className="relative min-h-[500px] flex items-center overflow-hidden">
                {coverImage?.image_url && <><ResponsiveImage src={coverImage.image_url} variantUrls={coverImage.variant_urls} width={coverImage.width} height={coverImage.height} alt="" className="absolute inset-0 w-full h-full object-cover" sizes="100vw" /><div className="absolute inset-0 bg-black/60"/></>}
                {!coverImage?.image_url && <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${s.accent}, ${hexToRgba(s.accent, 0.87)})` }} />}
                <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center text-white ${anim}`}>
                    <span className="text-sm font-semibold tracking-wider uppercase opacity-70">{tag}</span>
                    <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-8">{title}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                        {brand.mission && (
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                                <h3 className="font-bold text-lg mb-2">{missionLabel}</h3>
                                <p className="text-white/80 leading-relaxed">{brand.mission}</p>
                            </div>
                        )}
                        {brand.vision && (
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                                <h3 className="font-bold text-lg mb-2">{visionLabel}</h3>
                                <p className="text-white/80 leading-relaxed">{brand.vision}</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        );
    }

    // ── Split variant (default) ──
    return (
        <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={tag} title={title} config={config} s={s} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                    <div className={`space-y-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                        {brand.mission && (
                            <div className="relative pl-6 border-l-4 rounded-sm" style={{ borderColor: s.accent }}>
                                <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{missionLabel}</h3>
                                <p className="leading-relaxed" style={{ color: s.textSub }}>{brand.mission}</p>
                            </div>
                        )}
                        {brand.vision && (
                            <div className="relative pl-6 border-l-4 rounded-sm" style={{ borderColor: hexToRgba(s.accent, 0.5) }}>
                                <h3 className="text-xl font-bold mb-3" style={{ color: s.text }}>{visionLabel}</h3>
                                <p className="leading-relaxed" style={{ color: s.textSub }}>{brand.vision}</p>
                            </div>
                        )}
                        {!brand.mission && !brand.vision && brand.welcome_text && (
                            <p className="leading-relaxed text-lg" style={{ color: s.textSub }}>{brand.welcome_text}</p>
                        )}
                    </div>
                    <div className={`transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                        <MediaPanel />
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
